/**
 * Firestore refs that are accessed.
 */
import {
  CollectionReference,
  Firestore,
  collection,
  collectionGroup,
  doc,
} from "firebase/firestore";

const firestoreCollections = {
  restaurants: "restaurants-v1",
  menuUrlsV2: "menu-urls-v2", // subcollection name under restaurants
  themes: "themes", // subcollection name under restaurants
  termsAndConditions: "t-and-cs",
  paymentPlans: "restaurant-payment-plans",
  suppliers: "suppliers-v1",
  roleGrants: "role-grants",

  restaurantLegal: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/restaurantLegal/${restaurantUuid}`,

  locations: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/locations`,

  menuUrlsV2Collection: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/${firestoreCollections.menuUrlsV2}`,

  themesCollection: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/${firestoreCollections.themes}`,

  // Menus & Dishes
  restaurantMenus: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/restaurantMenus/${restaurantUuid}`,
  menus: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/menus`,
  menuSections: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/menuSections`,
  dishes: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/dishes`,
  products: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/products`,
  productUploadsCollectionId: "productUpload",
  productUploads: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/${firestoreCollections.productUploadsCollectionId}`,
  integrations: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/integrations`,
  restaurantSuppliers: (restaurantUuid: string) =>
    `${firestoreCollections.restaurants}/${restaurantUuid}/restaurant-suppliers`,
  managerRestaurantMenuFavorites: (uid: string, restaurantUuid: string) =>
    `restaurant-manager/${uid}/accessible-restaurants/${restaurantUuid}/menu-favorites`,
  managerRestaurantDishesViewed: (uid: string, restaurantUuid: string) =>
    `restaurant-manager/${uid}/accessible-restaurants/${restaurantUuid}/dishes-viewed`,
};

export class FirestoreRefs {
  public firestore: Firestore;

  constructor(firestore: Firestore) {
    this.firestore = firestore;
  }

  public restaurants = () =>
    collection(this.firestore, firestoreCollections.restaurants);
  public restaurant = (uuid: string) =>
    doc(this.firestore, firestoreCollections.restaurants, uuid);

  public locations = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.locations(restaurantUuid));

  public location = (restaurantUuid: string, locationId: string) =>
    doc(
      this.firestore,
      firestoreCollections.locations(restaurantUuid),
      locationId
    );

  // Menu URLs V2 (restaurant subcollection)
  public menuUrlsV2 = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.menuUrlsV2Collection(restaurantUuid)
    );

  public menuUrlV2 = (restaurantUuid: string, menuUrlId: string) =>
    doc(
      this.firestore,
      firestoreCollections.menuUrlsV2Collection(restaurantUuid),
      menuUrlId
    );

  /** Collection group for querying menu-urls-v2 across all restaurants. */
  public menuUrlsV2CollectionGroup = () =>
    collectionGroup(this.firestore, firestoreCollections.menuUrlsV2);

  // Themes (restaurant subcollection)
  public themes = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.themesCollection(restaurantUuid)
    );

  public theme = (restaurantUuid: string, themeId: string) =>
    doc(
      this.firestore,
      firestoreCollections.themesCollection(restaurantUuid),
      themeId
    );

  public termsAndConditions = (tcId: string) =>
    doc(this.firestore, firestoreCollections.termsAndConditions, tcId);

  public restaurantTermsAcceptance = (restaurantUuid: string) =>
    doc(this.firestore, firestoreCollections.restaurantLegal(restaurantUuid));

  // Payment Plan
  public paymentPlans = () =>
    collection(this.firestore, firestoreCollections.paymentPlans);

  public paymentPlan = (restaurantUuid: string) =>
    doc(this.firestore, firestoreCollections.paymentPlans, restaurantUuid);

  // Menus & Dishes
  // Menu related metadata at a restaurant level
  public restaurantMenu = (restaurantUuid: string) =>
    doc(this.firestore, firestoreCollections.restaurantMenus(restaurantUuid));
  public menus = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.menus(restaurantUuid));

  public menu = (restaurantUuid: string, menuId: string) =>
    doc(this.firestore, firestoreCollections.menus(restaurantUuid), menuId);

  public menuSections = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.menuSections(restaurantUuid)
    );

  public menuSection = (restaurantUuid: string, menuSectionId: string) =>
    doc(
      this.firestore,
      firestoreCollections.menuSections(restaurantUuid),
      menuSectionId
    );

  public dishes = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.dishes(restaurantUuid));

  public dish = (restaurantUuid: string, dishId: string) =>
    doc(this.firestore, firestoreCollections.dishes(restaurantUuid), dishId);

  public products = (restaurantUuid: string) =>
    collection(this.firestore, firestoreCollections.products(restaurantUuid));

  public product = (restaurantUuid: string, productId: string) =>
    doc(
      this.firestore,
      firestoreCollections.products(restaurantUuid),
      productId
    );

  public productUploads = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.productUploads(restaurantUuid)
    );

  public productUploadsCollectionGroup = () =>
    collectionGroup(
      this.firestore,
      firestoreCollections.productUploadsCollectionId
    );

  public productUpload = (restaurantUuid: string, productUploadId: string) =>
    doc(
      this.firestore,
      firestoreCollections.productUploads(restaurantUuid),
      productUploadId
    );

  // Integrations
  public integrations = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.integrations(restaurantUuid)
    );

  public integration = (restaurantUuid: string, integrationId: string) =>
    doc(
      this.firestore,
      firestoreCollections.integrations(restaurantUuid),
      integrationId
    );

  // for a given restaurant manager, this returns the list of restaurants that are accessible to them
  public managerAccessibleRestaurants = (uid: string): CollectionReference =>
    collection(
      this.firestore,
      `restaurant-manager/${uid}/accessible-restaurants`
    );

  public roleGrantsCollectionGroup = () =>
    collectionGroup(this.firestore, firestoreCollections.roleGrants);

  public managerRestaurantDishesViewed = (
    uid: string,
    restaurantId: string
  ): CollectionReference =>
    collection(
      this.firestore,
      firestoreCollections.managerRestaurantDishesViewed(uid, restaurantId)
    );

  public dishViewed = (uid: string, restaurantId: string, menuId: string) =>
    doc(
      this.firestore,
      firestoreCollections.managerRestaurantDishesViewed(uid, restaurantId),
      menuId
    );

  public managerRestaurantMenuFavorites = (
    uid: string,
    restaurantId: string
  ): CollectionReference =>
    collection(
      this.firestore,
      firestoreCollections.managerRestaurantMenuFavorites(uid, restaurantId)
    );

  public menuFavorite = (uid: string, restaurantId: string, menuId: string) =>
    doc(
      this.firestore,
      firestoreCollections.managerRestaurantMenuFavorites(uid, restaurantId),
      menuId
    );

  // Existence of this doc indicates that the user is an admin
  public adminUser = (uid: string) => doc(this.firestore, `admin-users/${uid}`);

  // Suppliers (Global/Central)
  public suppliers = () =>
    collection(this.firestore, firestoreCollections.suppliers);

  public supplier = (supplierId: string) =>
    doc(this.firestore, firestoreCollections.suppliers, supplierId);

  // Restaurant Suppliers
  public restaurantSuppliers = (restaurantUuid: string) =>
    collection(
      this.firestore,
      firestoreCollections.restaurantSuppliers(restaurantUuid)
    );

  public restaurantSupplier = (restaurantUuid: string, supplierId: string) =>
    doc(
      this.firestore,
      firestoreCollections.restaurantSuppliers(restaurantUuid),
      supplierId
    );
}

// The following allows for tree-shaking of the unused refs, and these aren't used in all apps.
export const dashboardAdminViewHistoryColRef = (
  firestore: Firestore,
  uid: string
) => collection(firestore, `admin-users/${uid}/dashboard-view-history`);
export const dashboardAdminViewHistoryRef = (
  firestore: Firestore,
  uid: string,
  restaurantUuid: string
) =>
  doc(firestore, `admin-users/${uid}/dashboard-view-history/${restaurantUuid}`);

// Other root collections
export const analyticsWatermarkColRef = (firestore: Firestore) =>
  collection(firestore, "restaurant-analytics-watermark");

// Other sub collections
export const restaurantSubColRef = (
  firestore: Firestore,
  restaurantUuid: string,
  collectionName: string
) =>
  collection(
    firestore,
    `${firestoreCollections.restaurants}/${restaurantUuid}/${collectionName}`
  );
export const locationSubColRef = (
  firestore: Firestore,
  restaurantUuid: string,
  locationId: string,
  collectionName: string
) =>
  collection(
    firestore,
    `${firestoreCollections.locations(
      restaurantUuid
    )}/${locationId}/${collectionName}`
  );
